import { motion } from 'framer-motion';
import SiteMeta from '../components/SiteMeta';
import { useNavigation } from '../context/NavigationContext';
import { useEffect, useState } from 'react';
import { fetchAboutContent } from '../lib/sanity';
import WebGLDisk from '../components/WebGLDisk';
import Footer from '../components/Footer';
import './About.css';

function About() {
  const { destinationPage, activePage, clearDestination } = useNavigation();
  const isDestination = destinationPage === 'about';
  const isActive = activePage === 'about';
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [diskSize, setDiskSize] = useState(150);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const data = await fetchAboutContent();
        setContent(data);
      } catch (err) {
        console.error('Error fetching content:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, []);

  useEffect(() => {
    const calculateDiskSize = () => {
      let size = (window.innerWidth / 9.5) - 7.2; // Convert rem to px (0.45rem ≈ 7.2px)
      
      // Increase size by 1.5x on mobile (under 768px)
      if (window.innerWidth <= 768) {
        size *= 1.5;
      }
      
      setDiskSize(Math.max(size, 50)); // Minimum size of 50px
    };

    calculateDiskSize();
    window.addEventListener('resize', calculateDiskSize);

    return () => window.removeEventListener('resize', calculateDiskSize);
  }, []);

  if (loading) return <div className="page">Loading...</div>;
  if (error) return <div className="page">Error: {error}</div>;
  if (!content) return <div className="page">No content found</div>;

  // Sample experience data - you can replace with your own content
  const experienceData = [
    {
      role: "Co-founder",
      years: "2018 - Present",
      agency: "Studio Inconnu"
    },
    {
      role: "Design Director",
      years: "2022 - Present",
      agency: "Landor"
    },
    {
      role: "Lead designer",
      years: "2020 - 2022",
      agency: "Fitch"
    },
    {
      role: "Lead designer",
      years: "2017 - 2020",
      agency: "AllofUs"
    },
    {
      role: "Art Director",
      years: "2016 - 2017",
      agency: "Random Studio"
    },
    {
      role: "Art Director",
      years: "2014 - 2017",
      agency: "Hi-ReS!"
    },
    {
      role: "Art Director",
      years: "2013 - 2014",
      agency: "Leonard",
      agencySub: "(Werkstatt)"
    },
    {
      role: "Designer",
      years: "2013",
      agency: "HKI",
      agencySub: "(Hellohikimori)"
    },
    {
      role: "Designer",
      years: "2012",
      agency: "Akaru"
    }
  ];

  return (
    <>
      <SiteMeta
        title="About - Robin Gardeur (le Shka)"
        description="Get in touch with Robin Gardeur (le Shka) - Director x Digital Artist with 15 years of experience. Co-Founder at Studio Inconnu, Design Director at Landor."
      />
      <motion.div
        className={`page ${isDestination ? 'page-transitioning' : ''} ${isActive ? 'page-active' : ''}`}
        initial={{ y: '100vh' }}
        animate={{ y: 0 }}
        exit={{ y: '-20vh' }}
        transition={{
          duration: 1,
          ease: [0.4, 0, 0.2, 1],
          type: "tween"
        }}
        onAnimationComplete={() => {
          if (isDestination) {
            clearDestination();
          }
        }}
      >
      <div className="title about-title">
        {/* <h1>{content.name}</h1> */}
        <div className="name">
        <div className="robin">
            <h1>R</h1>
            <WebGLDisk size={diskSize} />
            <h1>bin</h1>
          </div>
          <h1>Gardeur</h1>
        </div>
      
        <div className="links">
          <div className="socials">
            {content.socials?.map((social, index) => (
              <a key={index} href={social.url} className="link-mask" target="_blank" rel="noopener noreferrer">
                <div className="link-line">
                  <div className="link-text-top">{social.platform}</div>
                  <div className="link-text-bottom">{social.platform}</div>
                </div>
              </a>
            ))}
          </div>
          <div className="contacts">
            {content.contacts?.map((contact, index) => (
              <a 
                key={index} 
                href={contact.type === 'email' ? `mailto:${contact.value}` : `tel:${contact.value}`} 
                className="link-mask"
              >
                <div className="link-line">
                  <div className="link-text-top">{contact.value}</div>
                  <div className="link-text-bottom">{contact.value}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      

      <div className="bottom-content">
        <div className="sticky-paragraph">
          {content.paragraphs && content.paragraphs.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
        
        <div className="word-list">
          {experienceData.map((item, index) => (
            <div key={index} className="experience-item">
              <div className="experience-left">
                <div className="role">{item.role}</div>
                <div className="years">{item.years}</div>
              </div>
              <div className="experience-right">
                <div className="agency">
                  {item.agency}
                  {item.agencySub && <span className="agency-sub">{item.agencySub}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer content={content} />
      </motion.div>
    </>
  );
}

export default About;